import React from "react";
import {inject, observer} from "mobx-react";
import configuration from "../../configuration/configuration";
import styles from "./BandeauAerith.module.css";
import {FONDS, fondOuDefaut} from "./fonds";
import {fondActuel} from "./fonds/choixFond";

const heure = new Intl.DateTimeFormat("fr-FR", {hour: "2-digit", minute: "2-digit"});
const jour = new Intl.DateTimeFormat("fr-FR", {weekday: "long", day: "numeric", month: "long"});

/** Heure et date en français, remises à jour chaque seconde ; les deux points clignotent. */
function useHorloge() {
    const [maintenant, setMaintenant] = React.useState(() => new Date());
    React.useEffect(() => {
        const minuteur = window.setInterval(() => setMaintenant(new Date()), 1000);
        return () => window.clearInterval(minuteur);
    }, []);
    const [h, m] = heure.format(maintenant).split(":");
    return {h, m, visible: maintenant.getSeconds() % 2 === 0, date: jour.format(maintenant)};
}

/** Icône « pas de réseau » : arcs de wifi barrés. */
const IconeHorsLigne = () => (
    <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M2 8.8a15 15 0 0 1 20 0"/>
        <path d="M5 12.5a10 10 0 0 1 14 0"/>
        <path d="M8.5 16a5 5 0 0 1 7 0"/>
        <circle cx="12" cy="19.5" r="1" fill="currentColor"/>
        <path d="M3 3l18 18"/>
    </svg>
);

/** Avertissement affiché quand l'écran a perdu le serveur d'affichage : verre ambré, assorti à la plaque du logo. */
const HorsLigne = () => (
    <span className={styles.horsLigne} role="status"><IconeHorsLigne/>Connexion perdue</span>
);

/**
 * Bandeau d'Aerith (l'écran client) : un fond interchangeable, et par-dessus trois éléments permanents
 * (logo, heure et date, état de la connexion) que le fond ne modifie jamais.
 * Le bandeau des autres écrans (Horloge) n'est pas touché.
 *
 * @param fond      (aperçu) force l'identifiant du fond ; sinon le bandeau applique la règle : choix manuel du tableau
 *                  de bord s'il est en cours, rotation automatique sinon (voir fonds/choixFond.js et fonds/README.md)
 * @param logoUrl   adresse du logo ; par défaut, celui de l'entreprise
 * @param connecte  force l'état de connexion (aperçu) ; par défaut, celui du serveur d'affichage
 */
const BandeauAerith = inject("rootStore")(observer(({rootStore, fond, logoUrl, connecte}) => {
    const {h, m, visible, date} = useHorloge();
    const auth = rootStore.authStore;
    const enLigne = connecte !== undefined ? connecte : auth.isLoggedDash && auth.isLoggedEcho;
    const logo = logoUrl || (auth.logo ? configuration.API + "/magasin/svg/" + auth.logo : null);

    // Le bandeau se met à jour chaque seconde (horloge) : un changement de fond est donc pris en compte aussitôt.
    const idFond = fondOuDefaut(fond !== undefined ? fond : fondActuel({
        ids: Object.keys(FONDS),
        rotationMinutes: auth.fondRotationMinutes,
        choisi: auth.fondChoisi,
        expireA: auth.fondExpireA,
        exclus: auth.fondExclus,
        rotationActive: auth.fondRotationActive,
        maintenant: Date.now(),
    }));
    const Fond = FONDS[idFond].composant;
    // images de la galerie associées à ce fond par le personnel (adresses complètes) ; un fond qui n'en veut pas les ignore
    const images = (auth.fondImages[idFond] || []).map(image => configuration.API + image.url);

    return (
        <header className={styles.bandeau}>
            {/* la clé fait redémarrer l'apparition en douceur à chaque changement de fond */}
            <div key={idFond} className={styles.fond}><Fond images={images} reglages={auth.fondReglages[idFond]} horaires={auth.fondHoraires}/></div>

            {/* ---- éléments permanents : au-dessus du fond (z-index 1), identiques quel que soit le fond ---- */}
            <div className={styles.gauche}>
                {logo && (
                    <div className={styles.logoBoite}>
                        <img src={logo} alt="AkitaTek" className={styles.logo}/>
                    </div>
                )}
            </div>

            <div className={styles.centre}>
                <div className={styles.heure}>
                    {h}<span className={styles.points} style={{opacity: visible ? 1 : 0.25}}>:</span>{m}
                </div>
                <div className={styles.date}>{date}</div>
            </div>

            <div className={styles.droite}>
                {enLigne
                    ? <span className={styles.voyant} title="Connecté au serveur d'affichage"/>
                    : <HorsLigne/>}
            </div>
        </header>
    );
}));

export default BandeauAerith;
